import Form from '@rjsf/mui';
import type RjsfForm from '@rjsf/core';
import type { RJSFSchema, UiSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { Box, CircularProgress, Typography } from '@mui/material';
import { type ReactElement, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import MarketingStationLayout from 'app/shared-components/MarketingStationLayout';
import useHeaderActions from 'app/hooks/useHeaderActions';
import useHeaderTitle from 'app/hooks/useHeaderTitle';
import useNavigationHeader from 'app/hooks/useNavigationHeader';
import {
  PreviewIframe,
  type PreviewIframeHandle,
} from 'app/pages/PlusWebsites/components/PreviewIframe';
import { pageDetailsPath } from 'app/pages/PlusWebsites/paths';
import { UnsavedChangesDialog } from 'app/pages/PlusWebsites/components/UnsavedChangesDialog';
import { useGuardedSave } from 'app/pages/PlusWebsites/hooks/useGuardedSave';

import { fields } from '../../../components/fields/fields';
import { templates } from '../../../components/templates/templates';
import { widgets } from '../../../components/widgets/widgets';
import { createMenuBuilderValidate } from '../../../components/widgets/MenuBuilder/menuBuilderCustomValidate';

interface PageSectionEditorProps {
  title: string;
  schema: RJSFSchema;
  uiSchema: UiSchema;
  onSubmit: (formData: Record<string, unknown>) => Promise<unknown>;
  formData?: Record<string, unknown>;
  isLoading?: boolean;
  isSaving?: boolean;
  /**
   * A section that does not exist yet: its schema defaults are already savable content, so Save
   * has to be live before the user edits anything.
   */
  isNew?: boolean;
  previewSectionId?: string;
  previewSectionType?: string;
}

export function PageSectionEditor({
  title,
  schema,
  uiSchema,
  onSubmit,
  formData,
  isLoading = false,
  isSaving = false,
  isNew = false,
  previewSectionId,
  previewSectionType,
}: PageSectionEditorProps): ReactElement {
  const formRef = useRef<RjsfForm | null>(null);
  const previewRef = useRef<PreviewIframeHandle>(null);

  const { pageId = '' } = useParams<{ pageId: string }>();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const backPath = pageDetailsPath(pageId, searchParams);

  const hasPreview = !!(previewSectionId || previewSectionType);
  // Use the real section ID for edits, or the section type as a stable identifier for creates.
  const previewId = previewSectionId || previewSectionType || '';

  const refreshPreview = useCallback(() => {
    if (previewSectionId) previewRef.current?.notifyPropsUpdated();
  }, [previewSectionId]);

  const guard = useGuardedSave({
    formRef,
    onSubmit,
    backPath,
    isSaving,
    allowPristineSave: isNew,
    onSaved: refreshPreview,
  });

  // Saving a section pushes a fresh page-details entry, so history holds every add and
  // edit — back has to go up a level instead of replaying that trail.
  useNavigationHeader({
    title: 'Back',
    navigateTo: backPath,
    backToHistory: false,
    replace: true,
    onBeforeNavigate: guard.confirmNavigation,
  });
  useHeaderTitle(title);
  const websiteId = searchParams.get('websiteId') ?? '';

  useHeaderActions([
    ...(previewSectionId
      ? [
          {
            title: 'Preview section',
            type: 'button' as const,
            callback: () => {
              const params = new URLSearchParams({
                websiteId,
                pageId,
                organisationId: searchParams.get('organisationId') ?? '',
              });
              if (previewSectionId) params.set('sectionId', previewSectionId);
              if (previewSectionType) params.set('sectionType', previewSectionType);
              window.open(`/plus-websites/preview?${params}`, '_blank', 'noopener,noreferrer');
            },
            disabled: !websiteId,
          },
        ]
      : []),
    {
      title: isSaving ? 'Saving…' : 'Save changes',
      type: 'button',
      callback: () => formRef.current?.submit(),
      disabled: !guard.canSave || isSaving || isLoading,
    },
  ]);

  return (
    <MarketingStationLayout className="vtwo:flex vtwo:flex-col vtwo:md:flex-row vtwo:h-full vtwo:overflow-hidden">
      <Box className="vtwo:flex-1 vtwo:bg-stone-100 vtwo:overflow-hidden">
        <Box className="vtwo:h-full vtwo:overflow-hidden">
          {hasPreview ? (
            <PreviewIframe
              ref={previewRef}
              pageId={pageId}
              sectionId={previewSectionId}
              sectionType={previewSectionType}
            />
          ) : (
            <Box className="vtwo:flex vtwo:h-full vtwo:flex-col vtwo:items-center vtwo:justify-center vtwo:gap-2 vtwo:p-8 vtwo:text-center">
              <Typography className="vtwo:font-bold vtwo:text-typography-primary">
                Preview unavailable
              </Typography>
              <Typography variant="body2" className="vtwo:max-w-sm vtwo:text-gray-500">
                A live preview is not available for this section type.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box className="vtwo:w-full vtwo:md:w-[30%] vtwo:shrink-0 vtwo:bg-white vtwo:overflow-y-auto vtwo:p-8 vtwo:flex vtwo:flex-col vtwo:gap-8 vtwo:border-t vtwo:border-stone-200 vtwo:md:border-t-0 vtwo:md:border-l">
        {isLoading && (
          <Box className="vtwo:flex vtwo:justify-center vtwo:py-24">
            <CircularProgress size={24} />
          </Box>
        )}

        {!isLoading && (
          <Form
            ref={formRef}
            schema={schema}
            uiSchema={uiSchema}
            initialFormData={formData}
            templates={templates}
            widgets={widgets}
            fields={fields}
            validator={validator}
            customValidate={createMenuBuilderValidate(uiSchema)}
            // rjsf reports an id only for user edits; the initial defaults pass omits it, so
            // this does not mark a freshly loaded form as changed. During creation we still
            // forward the defaults to the preview so the iframe has something to render.
            onChange={({ formData }, id) => {
              if (id !== undefined) guard.markDirty();
              if (hasPreview && formData) {
                previewRef.current?.sendSectionData(
                  previewId,
                  formData,
                  previewSectionId ? undefined : previewSectionType
                );
              }
            }}
            onSubmit={() => guard.save()}
            onError={() =>
              enqueueSnackbar('Please fix the errors before saving', { variant: 'error' })
            }
            showErrorList={false}
            transformErrors={(errors) => errors.filter((e) => e.name !== 'additionalProperties')}
          >
            <button type="submit" hidden aria-label="Save" />
          </Form>
        )}
      </Box>

      <UnsavedChangesDialog {...guard.dialogProps} />
    </MarketingStationLayout>
  );
}
